import React, { useRef, useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { sendRemoveFromCartEvent } from "@/services/DataLayer/events";
import { AddonFieldProps, AddonsData } from "../types";
import styles from "./addonsfields.module.scss";

const CARD_AND_PRIORITY_ADDON_IDS = new Set(["card", "priority"]);
const INSURANCE_ADDON_ID = "insurance";

const withReviewAltAddonDefaults = (addon: AddonsData): AddonsData =>
  addon.identifier === INSURANCE_ADDON_ID ? { ...addon, value: "1" } : { ...addon, value: "0" };

const AddonsField = ({
  t,
  label,
  addons: initialAddons,
  value,
  onChange,
  hideCardAndPriorityAddons,
}: AddonFieldProps) => {
  const pathname = usePathname();
  const isReviewAlt = Boolean(pathname?.includes("/review/") && pathname?.includes("/alt"));
  const [addons, setAddons] = useState<AddonsData[]>([]);
  // Initial addon values (checked defaults come from the site-info `checked` flag
  // applied in buildAddons, PASGF-2744). Kept to restore card/priority defaults
  // when they are un-hidden after selecting a paid service.
  const checkedDefaultsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!Array.isArray(initialAddons) || initialAddons.length === 0) return;

    setAddons((prevAddons) => {
      if (prevAddons.length > 0) return prevAddons;

      const initializedAddons = initialAddons.map((addon) => {
        const mergedAddon = value.find((v) => v.identifier === addon.identifier) || addon;

        return isReviewAlt ? withReviewAltAddonDefaults(mergedAddon) : mergedAddon;
      });

      checkedDefaultsRef.current = initializedAddons.reduce<Record<string, string>>(
        (defaults, addon) => ({ ...defaults, [addon.identifier]: addon.value }),
        {}
      );

      return initializedAddons;
    });
  }, [initialAddons, isReviewAlt, value]);

  useEffect(() => {
    onChange(addons);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addons]);

  // Hidding the card addon means forcing its value to "0" so that it is not selected
  // despite being hidden. When un-hidden, the addon returns to its checked default.
  useEffect(() => {
    setAddons((prevAddons) => {
      if (prevAddons.length === 0) return prevAddons;

      return prevAddons.map((addon) => {
        if (!CARD_AND_PRIORITY_ADDON_IDS.has(addon.identifier)) return addon;
        if (isReviewAlt) return withReviewAltAddonDefaults(addon);

        return {
          ...addon,
          value: hideCardAndPriorityAddons
            ? "0"
            : (checkedDefaultsRef.current[addon.identifier] ?? "0"),
        };
      });
    });
  }, [hideCardAndPriorityAddons, isReviewAlt]);

  const handleOnClick = (identifier: string, ev: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = ev.target.checked ? "1" : "0";

    setAddons((prevAddons) =>
      prevAddons.map((addon) =>
        addon.identifier === identifier ? { ...addon, value: isChecked } : addon
      )
    );

    if (isChecked === "0") {
      const removedAddon = addons.find((addon) => addon.identifier === identifier);
      if (removedAddon) sendRemoveFromCartEvent(removedAddon);
    }
  };

  const sortedAddons = [...addons].sort((a, b) => a.order - b.order);

  return addons.length > 0 ? (
    <>
      <h2 className={styles.header}>{t[label]}</h2>

      {sortedAddons.map((addon) => {
        if (addon.identifier === "discount") return null;
        // The addons source is hard to reach, everything is tightly coupled, so this is a quick fix to hide the card addon when not needed
        if (hideCardAndPriorityAddons && CARD_AND_PRIORITY_ADDON_IDS.has(addon.identifier))
          return null;
        return (
          <div
            key={addon.identifier}
            className={`${styles.addon} ${addon.identifier === "tsa_precheck" ? styles.tsa_precheck : ""}`}>
            <div className={styles.addon__body}>
              <input
                data-testid="check-input"
                className="check__input"
                type="checkbox"
                name={addon.name}
                id={addon.name}
                onChange={(ev) => handleOnClick(addon.identifier, ev)}
                data-qa={`checkbox-${addon.name}`}
                checked={addon.value === "1"}
              />
              <label htmlFor={addon.name}>
                <strong>{t[addon.title]}</strong>
              </label>
              <strong className={styles.addon__body__price}>{addon.price}</strong>
            </div>
            <div
              className={styles.addon__footer}
              dangerouslySetInnerHTML={{ __html: t[addon.description] }}
            />
          </div>
        );
      })}
    </>
  ) : null;
};

export default AddonsField;
